import { DateOnly } from '@if/core/date'
import { EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import * as yup from '@if/core/yup'
import { WorksheetFormDefaults } from '@src/data/api/computation/form-defaults-api'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseWorksheetFormConfig, findMissingFinancingConfigKeys } from './BaseWorksheetForm.config'

// The form's draft shape: fields the user has not filled yet are null.
export interface BaseWorksheetFormValues {
  amountRequested: number | null
  paymentPlanId: string | null
  term: number | null
  paymentFrequency: EPaymentFrequency
  activationDate: DateOnly | null
  firstPaymentDate: DateOnly | null
  includeProtection: boolean
  isHighEstimate: boolean
}

export interface TermValidationContext {
  validTerms: Set<number>
}

export interface PlanValidationContext {
  validPlanIds: Set<string>
}

export const buildBaseWorksheetFormInitialValues = <T extends BaseWorksheetFormValues>(
  defaultValues: T,
  worksheet: BaseWorksheetDto | null,
  requestedLoanAmount: number | null,
  defaultPaymentPlanId?: EPaymentPlan,
): T => {
  return {
    ...defaultValues,
    amountRequested: worksheet?.amountRequested ?? requestedLoanAmount ?? defaultValues.amountRequested,
    paymentPlanId:
      (worksheet?.paymentPlanId as EPaymentPlan | undefined) ??
      defaultPaymentPlanId ??
      EPaymentPlan.RegularDailyInterests,
    term: worksheet?.term ?? defaultValues.term,
    paymentFrequency: worksheet?.paymentFrequency ?? defaultValues.paymentFrequency,
    activationDate: worksheet?.deliveryOn ? new DateOnly(worksheet.deliveryOn) : defaultValues.activationDate,
    firstPaymentDate: worksheet?.firstPaymentOn
      ? new DateOnly(worksheet.firstPaymentOn)
      : defaultValues.firstPaymentDate,
    includeProtection: worksheet?.includeInsurance || worksheet?.includeProtection || defaultValues.includeProtection,
    isHighEstimate: worksheet?.isHighEstimate ?? defaultValues.isHighEstimate,
  }
}

// Overlays the backend-resolved seed (the coherent granted amount + valid default term/plan
// for a coherent date pair) onto the client-built initial values. Fed to FormContainer's async
// defaultValues so the form initializes from the backend source of truth in one shot.
export const mergeWorksheetFormDefaults = <T extends BaseWorksheetFormValues>(
  base: T,
  defaults: WorksheetFormDefaults,
): T => ({
  ...base,
  amountRequested: defaults.amountRequested,
  paymentPlanId: defaults.paymentPlanId,
  term: defaults.term,
  paymentFrequency: defaults.paymentFrequency,
  activationDate: defaults.activationDate,
  firstPaymentDate: defaults.firstPaymentDate,
  includeProtection: defaults.includeProtection,
  isHighEstimate: defaults.isHighEstimate,
})

export const useBaseWorksheetFormInitialValues = <T extends BaseWorksheetFormValues>(
  defaultValues: T | null,
  worksheet: BaseWorksheetDto | null,
  requestedLoanAmount: number | null,
  defaultPaymentPlanId?: EPaymentPlan,
): T | undefined =>
  useMemo(() => {
    if (!defaultValues) return undefined
    return buildBaseWorksheetFormInitialValues(defaultValues, worksheet, requestedLoanAmount, defaultPaymentPlanId)
  }, [defaultPaymentPlanId, defaultValues, requestedLoanAmount, worksheet])

export const useBaseWorksheetFormFields = (config: BaseWorksheetFormConfig) => {
  const { t } = useTranslation()
  const {
    minLoanAmount: configMinLoanAmount,
    maxLoanAmount: configMaxLoanAmount,
    supportedPaymentFrequencies,
    expiresOn: expiresOnRaw,
    maxAmountFinanced,
    isSinglePayment,
  } = config

  return useMemo(() => {
    const supportedFrequencies = isSinglePayment ? [EPaymentFrequency.Single] : supportedPaymentFrequencies

    const minLoanAmount = configMinLoanAmount > 0 ? configMinLoanAmount : 1
    const loanAmountCaps: number[] = []
    if (configMaxLoanAmount > 0) loanAmountCaps.push(configMaxLoanAmount)
    if (maxAmountFinanced && maxAmountFinanced > 0) loanAmountCaps.push(maxAmountFinanced)
    const maxLoanAmount = loanAmountCaps.length > 0 ? Math.min(...loanAmountCaps) : null

    const today = DateOnly.today()
    const expiresOn = new DateOnly(expiresOnRaw)

    const defaultPaymentFrequency = isSinglePayment
      ? EPaymentFrequency.Single
      : supportedFrequencies.includes(EPaymentFrequency.Monthly)
        ? EPaymentFrequency.Monthly
        : supportedFrequencies[0]

    const amountRequestedBase = yup
      .number()
      .required()
      .min(minLoanAmount, t('worksheetCommon.errors.amountTooLow', { min: minLoanAmount }))
    const amountRequested =
      maxLoanAmount !== null
        ? amountRequestedBase
            .max(maxLoanAmount, t('worksheetCommon.errors.amountTooHigh', { max: maxLoanAmount }))
            .default(minLoanAmount)
        : amountRequestedBase.default(minLoanAmount)

    return {
      amountRequested,
      paymentPlanId: yup
        .string()
        .required()
        .test('plan-available', t('worksheetCommon.errors.paymentPlanNotAvailable'), function (value) {
          if (!value || value === (EPaymentPlan.RegularDailyInterests as string)) return true
          // Plan validity is owned by the runtime TermPlanOptions grid (published via context).
          // Until it has loaded, defer — the field loading gate blocks submit meanwhile.
          const ctx = this.options.context?.paymentPlanId as PlanValidationContext | undefined
          return ctx ? ctx.validPlanIds.has(value) : true
        })
        .default(''),
      term: yup
        .number()
        .integer()
        .nullable()
        .required()
        .test('term-available', function (value) {
          const ctx = this.options.context?.term as TermValidationContext | undefined
          if (!ctx || value == null || ctx.validTerms.has(value)) return true
          return this.createError({
            message:
              ctx.validTerms.size === 0
                ? t('worksheetCommon.errors.noValidTerm')
                : t('worksheetCommon.errors.pmtTooHigh'),
          })
        })
        .default(() => null),
      paymentFrequency: yup
        .mixed<EPaymentFrequency>()
        .required()
        .oneOf(supportedFrequencies, t('worksheetCommon.errors.paymentFrequencyNotSupported'))
        .default(defaultPaymentFrequency),
      activationDate: yup
        .dateOnly()
        .required()
        .test('not-in-past', t('worksheetCommon.errors.activationInPast'), (value) => {
          if (!value) return true
          return value >= today
        })
        .test('before-expiry', t('worksheetCommon.errors.activationAfterExpiry'), (value) => {
          if (!value) return true
          return value <= expiresOn
        })
        .default(() => DateOnly.today()),
      firstPaymentDate: yup
        .dateOnly()
        .nullable()
        .required()
        .default(() => null),
      includeProtection: yup.boolean().required().default(false),
      isHighEstimate: yup.boolean().required().default(false),
    }
  }, [
    configMinLoanAmount,
    configMaxLoanAmount,
    supportedPaymentFrequencies,
    maxAmountFinanced,
    expiresOnRaw,
    isSinglePayment,
    t,
  ])
}

export const useBaseWorksheetFormReady = (config: BaseWorksheetFormConfig): boolean => {
  const { supportedPaymentFrequencies, isSinglePayment } = config
  return useMemo(() => {
    if (findMissingFinancingConfigKeys(config).length > 0) return false
    if (!isSinglePayment && !supportedPaymentFrequencies?.length) return false
    return true
  }, [config, supportedPaymentFrequencies, isSinglePayment])
}
